import { components } from 'common';
import { useImmer } from 'use-immer';
import { getCurrentSystem, lang } from '@umu/ukit-lang';
import { isMobile } from '@umu/umu-ua';
import { Iconfont } from '@umu/umu-design';
import './aiSubtitleTranslateSettingPanel.scss';
import { LabelAndValueOptions } from '../interface';
import { LANG_TEXT_MAP } from '@umu/umu-lang-switch';
import React, { useEffect, useRef, useState } from 'react';
import {
    AiSubTitleCurrentOperation,
    MoreSettingCurrentOperation,
    aiSubTitleCurrentOperationTitleMap,
    aiSubtitleFontSizeOptions,
    aiSubtitlePositionOptions,
} from '../dataflow';

const { If, Checkbox, RadioList } = components;

type AiSubtitleTranslateSettingPanelProps = {
    visible: boolean;
    uControlBar: any;
    emitCustomEvent: any;
    isInMoreSetting?: boolean;
    setting: {[key: string]: any};
    setMoreCurrentOption?: (value: any) => void;
    setSetting: (value: {[key: string]: any})=> void;
};
type LANG_TEXT_MAP_KEY = keyof typeof LANG_TEXT_MAP;

const system = getCurrentSystem().toLocaleLowerCase();
const levelLayoutList = ['cn','jp','ko'];

function AiSubtitleTranslateSettingPanel(props: AiSubtitleTranslateSettingPanelProps) {
    const { uControlBar, isInMoreSetting, emitCustomEvent, setMoreCurrentOption,setting,setSetting } = props;

    const [optionsListMap, setOptionsListMap] = useImmer<{ [key: string]: LabelAndValueOptions[] }>({
        [AiSubTitleCurrentOperation.language]: [],
        [AiSubTitleCurrentOperation.fontSize]: aiSubtitleFontSizeOptions,
    });
    const [currentOperation, setCurrentOption] = useState<AiSubTitleCurrentOperation>(AiSubTitleCurrentOperation.all);
    const settingItemContainerRef = useRef<HTMLDivElement>(null);
    const settingPanelTitleRef = useRef<HTMLDivElement>(null);
    const titleAndOptionsTitleRef = useRef<HTMLDivElement>(null);
    const titleAndOptionsList = useRef<HTMLDivElement>(null);

    const handleIsShowLanguageCheckboxChange = (isChecked: boolean) => {
        setSetting({ ...setting, display_original: Number(isChecked) });
        emitCustomEvent('u-ai-subtitle:change_show_original', uControlBar.wrapContainer, {
            result: isChecked,
            transferTranslateSetting: { ...setting, display_original: Number(isChecked) },
        });
    };

    const handleChangeCurrentOperation = (operation: AiSubTitleCurrentOperation) => {
        setCurrentOption(operation);
    };

    const handleLanguageRadioChange = (data: IRadioItem) => {
        setSetting({ ...setting, original_pos: data.value });
        emitCustomEvent('u-ai-subtitle:change_pos', uControlBar.wrapContainer, {
            type: 'original_pos',
            value: data.value
        });
    };

    const handleTranslateRadioChange = (data: IRadioItem) => {
        setSetting({ ...setting, trans_pos: data.value });
        emitCustomEvent('u-ai-subtitle:change_pos', uControlBar.wrapContainer, {
            type: 'trans_pos',
            value: data.value
        });
    };

    const handleApplyCheckboxChange = (isChecked: boolean) => {
        setSetting({ ...setting, apply_to_learner: Number(isChecked) });
    };

    const handleChangeOptionsSelected = (index: number) => {
        const item = optionsListMap[currentOperation][index];
        if (currentOperation === AiSubTitleCurrentOperation.language) {
            setSetting({ ...setting, setup_lang: item.value });
            emitCustomEvent('u-ai-subtitle:change_setup_lang', uControlBar.wrapContainer, {
                result: item.value,
            });
        } else if (currentOperation === AiSubTitleCurrentOperation.fontSize) {
            setSetting({ ...setting, font_size: item.value });
            emitCustomEvent('u-ai-subtitle:change_font_size', uControlBar.wrapContainer, {
                font_size: item.value
            });
        }

        setOptionsListMap((draft) => {
            draft[currentOperation].forEach((item) => {
                item.isSelected = false;
            });
            draft[currentOperation][index].isSelected = true;
        });
    };

    const handleClickTitleOperation = () => {
        setMoreCurrentOption?.(MoreSettingCurrentOperation.all);
    };

    const computedSettingItemContainerHeight = () => {
        if (!settingItemContainerRef.current) {
            return;
        }
        const titleHeight = settingPanelTitleRef?.current?.offsetHeight || 0;
        settingItemContainerRef.current.style.height = `calc(100% - ${titleHeight}px)`;
    };

    const computedTitleAndOptionsListHeight = () => {
        if (!titleAndOptionsList.current) {
            return;
        }
        const titleHeight = titleAndOptionsTitleRef?.current?.offsetHeight || 0;
        titleAndOptionsList.current.style.height = `calc(100% - ${titleHeight}px)`;
    };

    const findLabelAndValueOptionsOfLabel = (type: AiSubTitleCurrentOperation, value: any) => {
        return optionsListMap[type].find((item) => String(item.value) === String(value))?.label || '';
    };

    const getTransLang = ()=>{
        const translateSetting = uControlBar?.config?.subtitleConfig?.translateSetting;

        if(Array.isArray(translateSetting?.trans_lang)){
            return translateSetting.trans_lang.filter((item: string)=> item);
        }
        return [];
    }

    const getLangItem = (id: string) => {
        return {
            value: id,
            label: LANG_TEXT_MAP[id as LANG_TEXT_MAP_KEY],
        };
    };

    const handleTransformOptionsList = ()=>{
        const translateSetting = uControlBar.config.subtitleConfig.translateSetting;

        /**
         * @description: 合并 lang 与 trans_lang 
         * @param {*} getTransLang
         * 逻辑: 分开处理, 避免 trans_lang 中存在 lang , 导致重复
         */        
        const langSelected:string[] = getTransLang().filter((item: any)=> item);
        if(translateSetting.lang && !langSelected.includes(translateSetting.lang)){
            langSelected.unshift(translateSetting.lang);
        }

        const langOptions:LabelAndValueOptions[] = langSelected.map(item=> {
            const _item = getLangItem(item);
            if((translateSetting?.lang ||'').split(',').includes(_item.value)){
                _item.label = _item.label + lang('（原始语言）')
            }
            return _item
        })
        
        setOptionsListMap({
            ...optionsListMap,
            [AiSubTitleCurrentOperation.language]: langOptions
        });
    }

    const titleAndOptioinsIsSelected = (currentOperation: AiSubTitleCurrentOperation,item: LabelAndValueOptions)=>{
        if(currentOperation === AiSubTitleCurrentOperation.language){
            return String(setting.setup_lang) === String(item.value);
        }
        return String(setting.font_size) === String(item.value)
    }

    const renderTitleAndOptions = () => {
        const data = optionsListMap[currentOperation];
        const title = aiSubTitleCurrentOperationTitleMap[currentOperation];

        if (!Array.isArray(data)) {
            return null;
        }
        return (
            <div className="setting-subtitle-title-and-options-panel">
                <div className="setting-subtitle-title-and-options-title" ref={titleAndOptionsTitleRef}>
                    <div
                    onClick={ () => handleChangeCurrentOperation(AiSubTitleCurrentOperation.all)}
                        className="setting-subtitle-title-and-options-title-operation"
                    >
                        <Iconfont type="Bigback" />
                    </div>
                    <div onClick={isMobile ? undefined : () => handleChangeCurrentOperation(AiSubTitleCurrentOperation.all)} className="setting-subtitle-title-and-options-title-content">{title}</div>
                </div>
                <div ref={titleAndOptionsList} className="setting-subtitle-title-and-options-options-list">
                    <div className="setting-subtitle-title-and-options-options-list-container">
                        {data.map((item, index) => {
                            return (
                                <div
                                    key={item.value}
                                    className={`setting-subtitle-title-and-options-options-item ${
                                        titleAndOptioinsIsSelected(currentOperation,item) ? 'active' : ''
                                    }`}
                                    onClick={() => handleChangeOptionsSelected(index)}
                                >
                                    <div className="setting-subtitle-title-and-options-options-item-selected-flag">
                                        <If condition={titleAndOptioinsIsSelected(currentOperation,item)}>
                                            <Iconfont type="Complete" />
                                        </If>
                                    </div>
                                    <div className="setting-subtitle-title-and-options-optioins-item-label">
                                        {item.label}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        computedSettingItemContainerHeight();
        computedTitleAndOptionsListHeight();
    }, [currentOperation]);

    useEffect(()=>{
        handleTransformOptionsList();
    },[uControlBar?.config?.subtitleConfig?.translateSetting,setting]);

    const isHiddenSetupSetting = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenSetupSetting ?? false);
    const isHiddenFontSize = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenFontSize ?? false);
    const isHiddenOriginalPos = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenOriginalPos ?? false)
    const isHiddenApplyToLearner = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenApplyToLearner ?? false)
    const isHiddenTransPos = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenTransPos ?? false);
    const isHiddenDisplayOrigin = (uControlBar?.config?.subtitleConfig?.translateSettingShow?.isHiddenDisplayOrigin ?? false);

    return (
        <div className={`u-ai-subtitle-translate-setting-panel ${isMobile ? 'm' : 'pc'}`}>
            {/* 设置项列表 */}
            <If condition={currentOperation === AiSubTitleCurrentOperation.all}>
                <div className="setting-panel">
                    <If condition={!!isInMoreSetting}>
                        <div ref={settingPanelTitleRef} className="setting-panel-title">
                            <div onClick={handleClickTitleOperation} className="setting-panel-title-operation">
                                <Iconfont type="Bigback" />
                            </div>
                            <div className="setting-panel-title-content">{lang('AI 字幕翻译设置')}</div>
                        </div>
                    </If>

                    <div ref={settingItemContainerRef} className="setting-item-container">
                        <If condition={!isHiddenSetupSetting}>
                            <div className={`setting-item-language ${!isMobile && !levelLayoutList.includes(system) ?'vertical': 'level'}`}>
                                <div className="setting-item-label">{lang('字幕语言')}</div>
                                <div
                                    className="setting-item-value-container"
                                    onClick={() => handleChangeCurrentOperation(AiSubTitleCurrentOperation.language)}
                                >
                                    <div className="setting-item-value">
                                        {findLabelAndValueOptionsOfLabel(AiSubTitleCurrentOperation.language, setting.setup_lang)}
                                    </div>
                                    <div className="setting-item-operation-icon">
                                        <Iconfont type="SmallMore" />
                                    </div>
                                </div>
                            </div>
                        </If>
                        
                        <If condition={!isHiddenDisplayOrigin && setting.lang !== setting.setup_lang}>
                            <div className="setting-item-is-show-init-language">
                                <div className="setting-item-checkbox">
                                    <Checkbox
                                        isDeepIcon={true}
                                        isChecked={Boolean(setting.display_original)}
                                        onChange={handleIsShowLanguageCheckboxChange}
                                    ></Checkbox>
                                    <span className="setting-item-checkbox-label">{lang('同时展示原始语言')}</span>
                                </div>
                            </div>
                        </If>
                        <If condition={!isHiddenOriginalPos && ( setting.lang === setting.setup_lang || Boolean(setting.display_original))}>
                        <div className="setting-item-language-position">
                            <div className="setting-item-label">{lang('原始语言字幕位置')}</div>
                            <div className="setting-item-radio-group">
                                <RadioList
                                    list={aiSubtitlePositionOptions}
                                    checked={setting.original_pos}
                                    onChange={handleLanguageRadioChange}
                                ></RadioList>
                            </div>
                        </div>
                        </If>
                        
                        <If condition={!isHiddenTransPos && setting.lang !== setting.setup_lang}>
                            <div className="setting-item-translate-position">
                                <div className="setting-item-label">{lang('翻译字幕位置')}</div>
                                <div className="setting-item-radio-group">
                                    <RadioList
                                        list={aiSubtitlePositionOptions}
                                        checked={setting.trans_pos}
                                        onChange={handleTranslateRadioChange}
                                    ></RadioList>
                                </div>
                            </div>
                        </If>

                        <If condition={!isHiddenFontSize}>
                        <div className="setting-item-font-size">
                            <div className="setting-item-label">{lang('字幕大小')}</div>
                            <div
                                className="setting-item-value-container"
                                onClick={() => handleChangeCurrentOperation(AiSubTitleCurrentOperation.fontSize)}
                            >
                                <div className="setting-item-value">
                                    {' '}
                                    {findLabelAndValueOptionsOfLabel(
                                        AiSubTitleCurrentOperation.fontSize,
                                        setting.font_size,
                                    )}
                                </div>
                                <div className="setting-item-operation-icon">
                                    <Iconfont type="SmallMore" />
                                </div>
                            </div>
                        </div>
                        </If>
                        
                        <If condition={!isHiddenApplyToLearner}>
                        <div className="setting-item-apply-student-default-setting">
                            <div className="setting-item-checkbox">
                                <Checkbox
                                    isDeepIcon={true}
                                    onChange={handleApplyCheckboxChange}
                                    isChecked={Boolean(setting.apply_to_learner)}
                                ></Checkbox>
                                <span className="setting-item-checkbox-label">{lang('应用为学员侧默认设置')}</span>
                            </div>
                        </div>
                        </If>
                        
                    </div>
                </div>
            </If>
            {/* 字幕语言专项设置 */}
            <If condition={currentOperation === AiSubTitleCurrentOperation.language}>{renderTitleAndOptions()}</If>
            {/* 字体大小专项设置 */}
            <If condition={currentOperation === AiSubTitleCurrentOperation.fontSize}>{renderTitleAndOptions()}</If>
        </div>
    );
}

export default AiSubtitleTranslateSettingPanel;